import React from 'react';
import { format, isToday } from 'date-fns';
import { ko } from 'date-fns/locale';
import { CalendarEvent, Holiday } from '../../types/api';

interface HolidayInfo {
  name: string;
  isPublicHoliday: boolean;
}

interface TodayScheduleSectionProps {
  selectedDate: Date;
  selectedDateEvents: CalendarEvent[];
  onAddClick?: () => void;
  onScheduleClick?: (event: CalendarEvent) => void;
  onScheduleDoubleClick?: (event: CalendarEvent) => void;
  draggableEventTypes?: string[];
  academyHoliday?: Holiday | null;
  publicHolidayInfo?: HolidayInfo | null;
}

const TodayScheduleSection: React.FC<TodayScheduleSectionProps> = ({
  selectedDate,
  selectedDateEvents,
  onAddClick,
  onScheduleClick,
  onScheduleDoubleClick,
  draggableEventTypes = ['regular', 'rescheduled', 'personal'],
  academyHoliday,
  publicHolidayInfo
}) => {
  const isTodaySelected = isToday(selectedDate);
  const dayOfWeek = selectedDate.getDay(); // 0=일요일, 6=토요일

  // 날짜 색상 결정: 공휴일 > 학원휴일 > 일요일 > 토요일 > 기본
  const getDateColorClass = () => {
    if (publicHolidayInfo?.isPublicHoliday) return 'public-holiday';
    if (academyHoliday) return 'academy-holiday';
    if (dayOfWeek === 0) return 'sunday';
    if (dayOfWeek === 6) return 'saturday';
    return '';
  };

  const dateLabel = isTodaySelected
    ? '오늘'
    : format(selectedDate, 'M월 d일 (E)', { locale: ko });

  // 휴일 정보 텍스트
  const getHolidayText = () => {
    if (academyHoliday) return academyHoliday.title;
    if (publicHolidayInfo) return publicHolidayInfo.name;
    return null;
  };

  const holidayText = getHolidayText();

  const isEventDraggable = (event: CalendarEvent): boolean => {
    const data = event.data as Record<string, any>;
    const scheduleType = data?.scheduleType || '';
    return draggableEventTypes.includes(scheduleType);
  };

  const handleDragStart = (e: React.DragEvent, event: CalendarEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/calendar-event', JSON.stringify(event));
    e.dataTransfer.setData('text/plain', JSON.stringify(event));
  };

  return (
    <div className="today-schedule-section">
      <div className="section-header">
        <div className="date-info">
          <h3 className={getDateColorClass()}>{dateLabel}</h3>
          {holidayText && (
            <span className={`holiday-label ${publicHolidayInfo?.isPublicHoliday ? 'public' : academyHoliday ? 'academy' : 'commemorative'}`}>
              {holidayText}
            </span>
          )}
        </div>
        {onAddClick && (
          <button
            type="button"
            className="add-schedule-btn"
            onClick={onAddClick}
            title="개인 일정 추가"
          >
            +
          </button>
        )}
      </div>
      {selectedDateEvents.length > 0 ? (
        <div className="schedule-items">
          {selectedDateEvents.map((event, index) => {
            const isDraggable = isEventDraggable(event);
            return (
            <div
              key={index}
              className={`schedule-item ${onScheduleClick ? 'clickable' : ''} ${isDraggable ? 'draggable' : ''}`}
              style={{ borderLeftColor: event.color }}
              draggable={isDraggable}
              onDragStart={(e) => isDraggable && handleDragStart(e, event)}
              onClick={() => onScheduleClick?.(event)}
              onDoubleClick={() => onScheduleDoubleClick?.(event)}
            >
              <div className="item-time">
                {format(new Date(event.start), 'HH:mm')}
              </div>
              <div className="item-content">
                <div className="item-title">{event.title}</div>
                <div className="item-duration">
                  {format(new Date(event.start), 'HH:mm')} - {format(new Date(event.end), 'HH:mm')}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="no-schedule">
          <span>일정 없음</span>
        </div>
      )}
    </div>
  );
};

export default TodayScheduleSection;
